import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, Crown, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | paid | failed
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); await refreshUser(); return; }
        if (data.status === "expired" || attempts > 8) { setStatus("failed"); return; }
      } catch { if (attempts > 8) { setStatus("failed"); return; } }
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen aurora bg-background text-foreground grid place-items-center px-6">
      <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} className="glass rounded-3xl p-10 max-w-md w-full text-center">
        {status === "checking" && (<><Loader2 className="animate-spin text-indigo-400 mx-auto mb-5" size={44} /><h1 className="font-display font-bold text-2xl">Confirming your payment…</h1><p className="text-muted-foreground mt-2">Just a moment.</p></>)}
        {status === "paid" && (<>
          <div className="h-20 w-20 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 grid place-items-center mx-auto mb-5 shadow-2xl shadow-yellow-500/40"><Crown size={40} className="text-white" /></div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight mb-2">Welcome to Premium! 🎉</h1>
          <p className="text-muted-foreground mb-7">You've unlocked unlimited coaching, all simulations, and every reward.</p>
          <Button data-testid="premium-continue-btn" onClick={() => navigate("/app")} className="w-full py-3 rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold">Start Exploring</Button>
        </>)}
        {status === "failed" && (<>
          <XCircle className="text-rose-400 mx-auto mb-5" size={48} />
          <h1 className="font-display font-bold text-2xl mb-2">Payment not completed</h1>
          <p className="text-muted-foreground mb-7">No charge was made. You can try again anytime.</p>
          <Button onClick={() => navigate("/app/premium")} className="w-full py-3 rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold">Back to Premium</Button>
        </>)}
      </motion.div>
    </div>
  );
}
